import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Bundle } from '@/api/types';
import { Card, ErrorNote, Money, Spinner } from '@/components/Common';
import { useAuth } from '@/lib/auth';
import { money } from '@/lib/format';

export function BundlesPage() {
  const { productId = '' } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['bundles', productId],
    queryFn: () => api.get<{ bundles: Bundle[] }>(
      `/api/admin/catalogue/products/${productId}/bundles`),
  });

  const sync = useMutation({
    mutationFn: () => api.post(`/api/admin/catalogue/stock/sync?productId=${productId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bundles', productId] }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  const bundles = data?.bundles ?? [];

  return (
    <div className="space-y-4">
      <Link to="/catalogue" className="text-sm text-ink-500 hover:underline">← Catalogue</Link>
      <Card
        title={`Bundles for product ${productId} — ${bundles.length}`}
        actions={can('ADMIN', 'OPS') && (
          <button type="button" className="btn-secondary" disabled={sync.isPending}
                  onClick={() => sync.mutate()}>
            {sync.isPending ? 'Syncing…' : 'Sync now'}
          </button>
        )}
      >
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-ink-200">
            <tr>
              <th className="th">Code</th><th className="th">Name</th>
              <th className="th">Description</th><th className="th text-right">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {bundles.map((bundle) => (
              <tr key={bundle.productCode} className="hover:bg-ink-50">
                <td className="td mono font-medium">{bundle.productCode}</td>
                <td className="td">{bundle.name}</td>
                <td className="td text-ink-600">{bundle.description ?? '—'}</td>
                <td className="td text-right">
                  <Money amount={money(bundle.value)} currency={bundle.currency} />
                </td>
              </tr>
            ))}
            {bundles.length === 0 && (
              <tr><td className="td py-8 text-center text-ink-500" colSpan={4}>
                No active bundles. HOT refreshes stock at midnight CAT; the sync runs at 00:45.
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
        <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
          A recharge for one of these must carry exactly this value — anything else is rejected
          before it reaches the upstream.
        </p>
      </Card>
    </div>
  );
}
